import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateMedicalOrganizationAddressRequestBody,
  MedicalOrganizationAddress,
  UpdateMedicalOrganizationAddressRequest,
} from '@interfaces/medicalOrganizationsAddresses.interface';

import {
  useCreateMedicalOrganizationAddressMutation,
  useUpdateMedicalOrganizationAddressMutation,
} from '@api/medicalOrganizationsAddresses.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from '@components/common/locations';

export const UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID =
  'upsertMedicalOrganizationAddress';

interface UpsertMedicalOrganizationAddressFormProps {
  medicalOrganizationId: string;
  medicalOrganizationAddress?: MedicalOrganizationAddress;
}

export default function UpsertMedicalOrganizationAddressForm({
  medicalOrganizationId,
  medicalOrganizationAddress,
}: UpsertMedicalOrganizationAddressFormProps) {
  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    name: medicalOrganizationAddress?.name || '',
    streetAddress1: medicalOrganizationAddress?.streetAddress1 || '',
    streetAddress2: medicalOrganizationAddress?.streetAddress2 || '',
    postalCode: medicalOrganizationAddress?.postalCode || '',
    locality: medicalOrganizationAddress?.locality || '',
    stateProvince: medicalOrganizationAddress?.stateProvince || '',
    countryCode: medicalOrganizationAddress?.countryCode || '',
    phone: medicalOrganizationAddress?.phone || '',
    email: medicalOrganizationAddress?.email || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: [
        'name',
        'locality',
        'streetAddress1',
        'streetAddress2',
      ],
      lowercaseFields: ['email'],
    },
  });

  // ==========================================================================
  // Api
  // ==========================================================================
  const [createMedicalOrganizationAddress, { isLoading: isLoadingCreate }] =
    useCreateMedicalOrganizationAddressMutation();
  const [updateMedicalOrganizationAddress, { isLoading: isLoadingUpdate }] =
    useUpdateMedicalOrganizationAddressMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onCancelClick = () => {
    closeModal(UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues = {
        name: values.name,
        streetAddress1: values.streetAddress1,
        streetAddress2:
          values.streetAddress2.trim() !== '' ? values.streetAddress2 : null,
        postalCode:
          values.postalCode.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.postalCode
              : null
            : null,
        locality: values.locality,
        stateProvince:
          values.stateProvince.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.stateProvince
              : null
            : null,
        countryCode: values.countryCode,
        phone: values.phone.trim() !== '' ? values.phone : null,
        email: values.email.trim() !== '' ? values.email : null,
      };

      if (medicalOrganizationAddress) {
        const data: UpdateMedicalOrganizationAddressRequest = {
          id: medicalOrganizationAddress.id,
          body: mappedValues,
        };

        await updateMedicalOrganizationAddress(data).unwrap();

        notifications.show({
          title: 'Indirizzo organizzazione medica modificato',
          message: 'Indirizzo organizzazione medica modificato con successo.',
        });
      } else {
        const data: CreateMedicalOrganizationAddressRequestBody = {
          ...mappedValues,
          medicalOrganizationId,
        };

        await createMedicalOrganizationAddress(data).unwrap();

        notifications.show({
          title: 'Indirizzo organizzazione medica creato',
          message: 'Nuovo indirizzo organizzazione medica creato con successo.',
        });
      }

      closeModal(UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // TODO: migliorare selezione indirizzi

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
        <NationSelector required {...form.getInputProps('countryCode')} />
        {form.values.countryCode === 'IT' ? (
          <>
            <ProvinceSelector
              required
              {...form.getInputProps('stateProvince')}
            />
            <CitySelector
              required
              province={form.values.stateProvince}
              {...form.getInputProps('locality')}
              disabled={form.values.stateProvince === ''}
            />
            <ZipSelector
              required
              city={form.values.locality}
              {...form.getInputProps('postalCode')}
              disabled={form.values.locality === ''}
            />
          </>
        ) : (
          <>
            <TextInput
              required
              label="Città"
              {...form.getInputProps('locality')}
            />
          </>
        )}

        <TextInput
          required
          label="Indirizzo"
          {...form.getInputProps('streetAddress1')}
        />
        <TextInput
          label="Indirizzo (2)"
          {...form.getInputProps('streetAddress2')}
        />
        <TextInput
          type="tel"
          label="Telefono"
          {...form.getInputProps('phone')}
        />
        <TextInput
          type="email"
          label="Email"
          {...form.getInputProps('email')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs" ta="right">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
